import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAppConfig } from '@/hooks/useShared';
import NotificationService, { type DiagnosticEntry } from '@/services/notification.service';
import { Banner, LoadingSpinner } from '@/components/ui';
import { runtimeEnv } from '@/lib/runtime-env';
import type { TelegramEvent } from '@/lib/types/events';

const WORKER_BASE_URL = 'https://poker-knights-3.eranhor.workers.dev';

const s = {
  page: { minHeight: '100vh', backgroundColor: '#f5f7f8', padding: '0 16px 180px' },
  container: { maxWidth: 400, margin: '0 auto', paddingTop: 16 },
  hero: { backgroundColor: '#e8f5f0', border: '1px solid rgba(0,104,85,0.12)', borderRadius: 16, padding: 20, marginBottom: 16 },
  heroLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 2, color: '#006855', opacity: 0.7, marginBottom: 4 },
  heroTitle: { fontSize: 22, fontWeight: 800, color: '#2c2f30', marginBottom: 12, lineHeight: 1.2 },
  inputLabel: { fontSize: 11, fontWeight: 700, color: '#595c5d', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: 1 },
  textarea: { width: '100%', minHeight: 120, backgroundColor: '#f5f7f8', border: '1px solid #e5e9ea', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 600, color: '#2c2f30', outline: 'none', fontFamily: 'inherit', resize: 'vertical' as const },
  input: { width: '100%', backgroundColor: '#f5f7f8', border: '1px solid #e5e9ea', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 600, color: '#2c2f30', outline: 'none', fontFamily: 'inherit', marginBottom: 12 },
  primaryBtn: { width: '100%', backgroundColor: '#006855', color: '#fff', border: 'none', borderRadius: 14, padding: '16px 24px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: 2, cursor: 'pointer', textAlign: 'center' as const, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 },
  msgCard: { backgroundColor: '#fff', borderRadius: 12, padding: 12, border: '1px solid #e5e9ea', marginBottom: 8 },
  msgHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  msgUser: { fontSize: 12, fontWeight: 800, color: '#006855' },
  msgTime: { fontSize: 10, color: '#999' },
  msgText: { fontSize: 13, color: '#2c2f30', lineHeight: 1.4 },
};

export function TelegramTestPage() {
  const { firebaseUser, user } = useAuth();
  const { config } = useAppConfig();

  const chatOptions = React.useMemo(() => {
    return (config?.telegram_chat_configs || []).map(c => ({
      label: c.label,
      id: Number(c.chat_id),
    }));
  }, [config]);

  const defaultChatId = chatOptions[0]?.id || -5011313718;

  const [eventType, setEventType] = useState<TelegramEvent['type']>('TEST_MESSAGE');
  const [payload, setPayload] = useState<any>({ message: '', mode: 'normal', playersRaw: '' });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [receivedMessages, setReceivedMessages] = useState<any[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [cleanupParams, setCleanupParams] = useState({ keepCount: '', keepSince: '' });
  const [cleaning, setCleaning] = useState(false);
  const [diagEntries, setDiagEntries] = useState<DiagnosticEntry[]>([]);
  const [outboxCount, setOutboxCount] = useState(0);
  const [workerOnline, setWorkerOnline] = useState<boolean | null>(null);
  const [checkingWorker, setCheckingWorker] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<number>(defaultChatId);
  const [beemPreview, setBeemPreview] = useState<{
    payerName: string;
    payerTelegram: string;
    payeeName: string;
    payeeTelegram: string;
    payeeBeem: string;
    amount: string;
    gameNumber: string;
    gameId: string;
  }>({ payerName: '', payerTelegram: '', payeeName: '', payeeTelegram: '', payeeBeem: '', amount: '', gameNumber: '', gameId: '' });
  const [beemPreviewResult, setBeemPreviewResult] = useState<{
    groupMsg: string;
    nudgeMsg: string;
    url: string;
    plainText: string;
  } | null>(null);
  const [sendingPreview, setSendingPreview] = useState(false);

  // Autopopulate payer from logged-in user
  useEffect(() => {
    if (user) {
      setBeemPreview(prev => ({
        ...prev,
        payerName: user.name || prev.payerName,
        payerTelegram: user.telegram_username || prev.payerTelegram,
      }));
    }
  }, [user]);

  const getAdminToken = async (): Promise<string | null> => {
    // Local dev admin bypass (dev/test environments only).
    if (runtimeEnv.allowLocalAdminBypass) {
      const uid = firebaseUser?.uid ?? 'localhost-uid';
      return `devtoken-${uid}`;
    }

    // 2. Prefer real Firebase ID token.
    try {
      return await (firebaseUser as any)?.getIdToken?.() ?? null;
    } catch {
      return null;
    }
  };

  const fetchMessages = async () => {
    const token = await getAdminToken();
    if (!token) {
      setStatus({ type: 'error', text: 'This page requires a signed-in admin.' });
      return;
    }
    setLoadingMsgs(true);
    try {
      const response = await fetch(`${WORKER_BASE_URL}/api/notifications/messages`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setReceivedMessages(data);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const refreshDiag = () => {
    setDiagEntries(NotificationService.getDiagnostics());
    try {
      const raw = localStorage.getItem('telegram_notification_outbox');
      setOutboxCount(raw ? JSON.parse(raw).length : 0);
    } catch {
      setOutboxCount(0);
    }
  };

  const checkWorker = async () => {
    setCheckingWorker(true);
    try {
      const res = await fetch(`${WORKER_BASE_URL}/api/health`, {
        cache: 'no-store',
      });
      setWorkerOnline(res.ok);
    } catch {
      setWorkerOnline(false);
    } finally {
      setCheckingWorker(false);
    }
  };

  const handleEventTypeChange = (type: TelegramEvent['type']) => {
    setEventType(type);
  };

  const handleCleanup = async () => {
    if (!confirm('Are you sure you want to delete these messages? This action cannot be undone.')) return;

    const token = await getAdminToken();
    if (!token) {
      setStatus({ type: 'error', text: 'This page requires a signed-in admin.' });
      return;
    }

    setCleaning(true);
    try {
      const response = await fetch(`${WORKER_BASE_URL}/api/notifications/cleanup`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keepCount: cleanupParams.keepCount ? Number(cleanupParams.keepCount) : undefined,
          keepSince: cleanupParams.keepSince || undefined,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatus({ type: 'success', text: `Deleted ${data.deletedCount} messages.` });
        fetchMessages();
      } else {
        const err = await response.json();
        throw new Error(err.message || 'Cleanup failed');
      }
    } catch (err: any) {
      setStatus({ type: 'error', text: err?.message || 'Failed to cleanup messages.' });
    } finally {
      setCleaning(false);
    }
  };
 
  const handleSend = async () => {
    setSending(true);
    setStatus(null);
    try {
      const finalPayload = { ...payload, chatId: selectedChatId };
      if (eventType === 'GAME_STARTED') {
        if (payload.playersRaw && payload.playersRaw.trim() !== '') {
          try {
            finalPayload.players = JSON.parse(payload.playersRaw);
          } catch (e) {
            throw new Error('Invalid JSON in players field');
          }
        } else {
          finalPayload.players = [];
        }
        delete finalPayload.playersRaw;
      } else if (eventType === 'GAME_ENDED') {
        if (payload.resultsRaw && payload.resultsRaw.trim() !== '') {
          try {
            finalPayload.results = JSON.parse(payload.resultsRaw);
          } catch (e) {
            throw new Error('Invalid JSON in results field');
          }
        } else {
          finalPayload.results = [];
        }
        delete finalPayload.resultsRaw;
      }
      console.log('[TelegramTestPage] Sending event:', { type: eventType, payload: finalPayload });
      await NotificationService.sendEvent({
        type: eventType,
        payload: finalPayload,
      });
      setStatus({ type: 'success', text: 'Event queued for delivery!' });
    } catch (err: any) {
      setStatus({ type: 'error', text: err?.message || 'Failed to send event.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.hero}>
          <div style={s.heroLabel}>Bot Diagnostics</div>
          <div style={s.heroTitle}>Telegram Test</div>
          <p style={{ fontSize: 13, color: '#595c5d', lineHeight: 1.5 }}>
            Send a custom message to the configured Telegram group to verify bot connectivity and formatting.
          </p>
        </div>

        <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e5e9ea' }}>
          <div style={s.inputLabel}>Event Type</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {(['TEST_MESSAGE', 'GAME_STARTED', 'BUY_IN_ADDED', 'SETTLEMENT_PUBLISHED', 'GAME_ENDED'] as const).map((type) => (
              <button
                key={type}
                onClick={() => handleEventTypeChange(type)}
                style={{
                  padding: '6px 12px',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase' as const,
                  borderRadius: 8,
                  cursor: 'pointer',
                  border: `1px solid ${eventType === type ? '#006855' : '#e5e9ea'}`,
                  backgroundColor: eventType === type ? '#e8f5f0' : '#fff',
                  color: eventType === type ? '#006855' : '#595c5d',
                  transition: 'all 0.2s',
                }}
              >
                {type.replace('_', ' ')}
              </button>
            ))}
          </div>

          <div style={s.inputLabel}>Target Chat</div>
          <select
            value={selectedChatId}
            onChange={(e) => setSelectedChatId(Number(e.target.value))}
            style={{ ...s.input, marginBottom: 12, cursor: 'pointer' }}
          >
            {chatOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label} — {opt.id}
              </option>
            ))}
          </select>

          <div style={s.inputLabel}>Payload</div>
          {eventType === 'TEST_MESSAGE' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['normal', 'philosophy'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setPayload({ ...payload, mode: m })}
                    style={{
                      flex: 1,
                      padding: '8px',
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase' as const,
                      borderRadius: 8,
                      cursor: 'pointer',
                      border: `1px solid ${payload.mode === m ? '#006855' : '#e5e9ea'}`,
                      backgroundColor: payload.mode === m ? '#e8f5f0' : '#fff',
                      color: payload.mode === m ? '#006855' : '#595c5d',
                      transition: 'all 0.2s',
                    }}
                  >
                    {m === 'normal' ? 'Normal' : 'Philosophy'}
                  </button>
                ))}
              </div>
              <textarea 
                style={s.textarea}
                value={payload.message}
                onChange={(e) => setPayload({ ...payload, message: e.target.value })}
                placeholder="Type your test message here..."
                disabled={sending}
              />
            </div>
          )}

           {eventType === 'GAME_STARTED' && (
             <div style={{ display: 'flex', flexDirection: 'column' }}>
               <input style={s.input} placeholder="Game ID" value={payload.gameId} onChange={(e) => setPayload({ ...payload, gameId: e.target.value })} disabled={sending} />
               <input style={s.input} placeholder="Venue" value={payload.venue} onChange={(e) => setPayload({ ...payload, venue: e.target.value })} disabled={sending} />
               <input style={s.input} type="date" value={payload.date} onChange={(e) => setPayload({ ...payload, date: e.target.value })} disabled={sending} />
               <input style={s.input} type="time" value={payload.time} onChange={(e) => setPayload({ ...payload, time: e.target.value })} disabled={sending} />
               <div style={s.inputLabel}>{"Players (JSON array of {name, telegram_username})"}</div>
               <textarea 
                 style={s.textarea} 
                 placeholder='[{"name": "John", "telegram_username": "john_doe"}, {"name": "Jane", "telegram_username": null}]' 
                 value={payload.playersRaw || ''} 
                 onChange={(e) => setPayload({ ...payload, playersRaw: e.target.value })} 
                 disabled={sending} 
               />
             </div>
           )}

          {eventType === 'BUY_IN_ADDED' && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <input style={s.input} placeholder="Player Name" value={payload.playerName} onChange={(e) => setPayload({ ...payload, playerName: e.target.value })} disabled={sending} />
              <input style={s.input} type="number" placeholder="Amount" value={payload.amount} onChange={(e) => setPayload({ ...payload, amount: Number(e.target.value) })} disabled={sending} />
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {(['digital', 'cash'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setPayload({ ...payload, type: t })}
                    style={{
                      flex: 1,
                      padding: '8px',
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase' as const,
                      borderRadius: 8,
                      cursor: 'pointer',
                      border: `1px solid ${payload.type === t ? '#006855' : '#e5e9ea'}`,
                      backgroundColor: payload.type === t ? '#e8f5f0' : '#fff',
                      color: payload.type === t ? '#006855' : '#595c5d',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

           {eventType === 'SETTLEMENT_PUBLISHED' && (
             <div style={{ display: 'flex', flexDirection: 'column' }}>
               <input style={s.input} placeholder="Game ID" value={payload.gameId} onChange={(e) => setPayload({ ...payload, gameId: e.target.value })} disabled={sending} />
               <textarea 
                 style={s.textarea}
                 value={payload.settlementSummary}
                 onChange={(e) => setPayload({ ...payload, settlementSummary: e.target.value })}
                 placeholder="Settlement Summary (e.g. John: +100, Jane: -100)"
                 disabled={sending}
               />
             </div>
           )}
           
           {eventType === 'GAME_ENDED' && (
             <div style={{ display: 'flex', flexDirection: 'column' }}>
               <div style={s.inputLabel}>Results (JSON array of {'{name, telegram_username, net}'})</div>
               <textarea 
                 style={s.textarea}
                 value={payload.resultsRaw || ''}
                 onChange={(e) => setPayload({ ...payload, resultsRaw: e.target.value })}
                 placeholder='[{"name": "John", "telegram_username": "john_doe", "net": 45.0}, {"name": "Jane", "telegram_username": null, "net": -20.0}]'
                 disabled={sending}
               />
             </div>
           )}
          
          <button 
            onClick={handleSend}
            disabled={sending}
            style={{ ...s.primaryBtn, opacity: sending ? 0.5 : 1 }}
          >
            {sending ? <LoadingSpinner /> : `Simulate ${eventType.replace('_', ' ')}`}
          </button>
        </div>
 
        <div style={{ marginTop: 24 }}>
          <div style={s.inputLabel}>Received Messages (Bot Tagged)</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <p style={{ fontSize: 11, color: '#595c5d' }}>Messages from Telegram chat where bot was tagged.</p>
            <button 
              onClick={fetchMessages} 
              disabled={loadingMsgs}
              style={{ fontSize: 10, fontWeight: 700, color: '#006855', cursor: 'pointer', background: 'none', border: 'none', textDecoration: 'underline' }}
            >
              {loadingMsgs ? '...' : 'Refresh'}
            </button>
          </div>
          
           {receivedMessages.length === 0 ? (
             <div style={{ textAlign: 'center', padding: 20, backgroundColor: '#fff', borderRadius: 16, border: '1px solid #e5e9ea', color: '#999', fontSize: 13 }}>
               No messages received yet.
             </div>
           ) : (
             receivedMessages.map((msg) => (
               <div key={msg.id} style={s.msgCard}>
                 <div style={s.msgHeader}>
                   <span style={s.msgUser}>{msg.from} (@{msg.username})</span>
                   <span style={s.msgTime}>{new Date(msg.timestamp).toLocaleString()}</span>
                 </div>
                 <div style={s.msgText}>{msg.text}</div>
               </div>
             ))
           )}
         </div>
  
         <div style={{ marginTop: 24, padding: 20, backgroundColor: '#fff', borderRadius: 16, border: '1px solid #e5e9ea' }}>
           <div style={s.inputLabel}>Cleanup Messages</div>
           <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
             <input 
               style={s.input} 
               type="number" 
               placeholder="Keep last X messages" 
               value={cleanupParams.keepCount} 
               onChange={(e) => setCleanupParams({ ...cleanupParams, keepCount: e.target.value, keepSince: '' })} 
             />
             <div style={{ textAlign: 'center', fontSize: 10, color: '#999', fontWeight: 700 }}>OR</div>
             <input 
               style={s.input} 
               type="date" 
               value={cleanupParams.keepSince} 
               onChange={(e) => setCleanupParams({ ...cleanupParams, keepSince: e.target.value, keepCount: '' })} 
             />
             <button 
               onClick={handleCleanup} 
               disabled={cleaning || (!cleanupParams.keepCount && !cleanupParams.keepSince)}
               style={{ ...s.primaryBtn, backgroundColor: '#dc3545', marginTop: 8 }}
             >
                {cleaning ? <LoadingSpinner /> : 'Delete Old Messages'}
             </button>
           </div>
           </div>

            <div style={{ marginTop: 24, padding: 20, backgroundColor: '#fff', borderRadius: 16, border: '1px solid #e5e9ea' }}>
              <div style={s.inputLabel}>Beem Link Preview</div>
              <p style={{ fontSize: 11, color: '#595c5d', marginBottom: 12 }}>
                Preview how settlement messages will look with Beem payment links before sending to the group.
              </p>

              <button
                onClick={async () => {
                  setSendingPreview(true);
                  setStatus(null);
                  try {
                    const testPayload = {
                      message: `🧪 Test message from Poker Knights to ${chatOptions.find(c => c.id === selectedChatId)?.label || 'Unknown'} chat`,
                      chatId: selectedChatId,
                    };
                    console.log('[TelegramTestPage] Sending TEST_SETTLEMENT:', testPayload);
                    await NotificationService.sendEvent({
                      type: 'TEST_SETTLEMENT',
                      payload: testPayload,
                    });
                    const chatLabel = chatOptions.find(c => c.id === selectedChatId)?.label || 'Unknown';
                    setStatus({ type: 'success', text: `Test message sent to ${chatLabel} chat (${selectedChatId})` });
                  } catch (err: any) {
                    setStatus({ type: 'error', text: err?.message || 'Failed to send test message.' });
                  } finally {
                    setSendingPreview(false);
                  }
                }}
                disabled={sendingPreview}
                style={{ ...s.primaryBtn, marginTop: 0, marginBottom: 16, backgroundColor: '#4a90d9' }}
              >
                {sendingPreview ? <LoadingSpinner /> : 'Send Test Message'}
              </button>

              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
               <input
                 style={{ ...s.input, marginBottom: 0, flex: 1 }}
                 placeholder="Payer name"
                 value={beemPreview.payerName}
                 onChange={(e) => setBeemPreview({ ...beemPreview, payerName: e.target.value })}
               />
               <input
                 style={{ ...s.input, marginBottom: 0, flex: 1 }}
                 placeholder="Payer @handle (optional)"
                 value={beemPreview.payerTelegram}
                 onChange={(e) => setBeemPreview({ ...beemPreview, payerTelegram: e.target.value })}
               />
             </div>
             <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
               <input
                 style={{ ...s.input, marginBottom: 0, flex: 1 }}
                 placeholder="Payee name"
                 value={beemPreview.payeeName}
                 onChange={(e) => setBeemPreview({ ...beemPreview, payeeName: e.target.value })}
               />
               <input
                 style={{ ...s.input, marginBottom: 0, flex: 1 }}
                 placeholder="Payee @handle (optional)"
                 value={beemPreview.payeeTelegram}
                 onChange={(e) => setBeemPreview({ ...beemPreview, payeeTelegram: e.target.value })}
               />
             </div>
             <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
               <input
                 style={{ ...s.input, marginBottom: 0, flex: 1 }}
                 placeholder="Payee Beem ID (e.g. eyalpat)"
                 value={beemPreview.payeeBeem}
                 onChange={(e) => setBeemPreview({ ...beemPreview, payeeBeem: e.target.value })}
               />
               <input
                 style={{ ...s.input, marginBottom: 0, flex: 1 }}
                 type="number"
                 placeholder="Amount ($)"
                 value={beemPreview.amount}
                 onChange={(e) => setBeemPreview({ ...beemPreview, amount: e.target.value })}
               />
             </div>
             <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
               <input
                 style={{ ...s.input, marginBottom: 0, flex: 1 }}
                 placeholder="Game # (optional)"
                 value={beemPreview.gameNumber}
                 onChange={(e) => setBeemPreview({ ...beemPreview, gameNumber: e.target.value })}
               />
             </div>

               <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => {
                    const amount = Number(beemPreview.amount);
                    if (!beemPreview.payeeBeem || !amount || !beemPreview.payerName || !beemPreview.payeeName) return;
                    const cents = Math.round(amount * 100);
                    const url = `https://www.beemit.com.au/app/pay?handle=${encodeURIComponent(beemPreview.payeeBeem)}&amount=${cents}&description=Poker%20Payment`;

                    const payerHandle = beemPreview.payerTelegram.trim().replace(/^@/, '');
                    const payeeHandle = beemPreview.payeeTelegram.trim().replace(/^@/, '');
                    const payerDisplay = payerHandle
                      ? `<a href="tg://user?id=123">${beemPreview.payerName}</a>`
                      : beemPreview.payerName;
                    const payeeDisplay = payeeHandle
                      ? `<a href="tg://user?id=456">${beemPreview.payeeName}</a>`
                      : beemPreview.payeeName;

                    const gameNumSuffix = beemPreview.gameNumber.trim()
                      ? ` (Game #${String(beemPreview.gameNumber).padStart(3, '0')})`
                      : '';

                    const groupMsg = `${payerDisplay} owes $${amount} to ${payeeDisplay}. React with 👍 to confirm.${gameNumSuffix}\n<a href="${url}">Pay via Beem</a>`;
                    const nudgeMsg = `Hey ${beemPreview.payerName}, you owe $${amount} to ${beemPreview.payeeName}${gameNumSuffix}. Tap here to confirm.\n<a href="${url}">Pay via Beem</a>`;

                    setBeemPreviewResult({ groupMsg, nudgeMsg, url, plainText: '' });
                  }}
                  disabled={!beemPreview.payeeBeem || !beemPreview.amount || !beemPreview.payerName || !beemPreview.payeeName}
                  style={{ ...s.primaryBtn, marginTop: 0, flex: 1 }}
                >
                  Preview Messages
                </button>
                  <button
                    onClick={async () => {
                      if (!beemPreviewResult) return;
                      setSendingPreview(true);
                      setStatus(null);
                      try {
                        const publishPayload = {
                          message: beemPreviewResult.groupMsg,
                          chatId: selectedChatId,
                        };
                        console.log('[TelegramTestPage] Publishing to group:', publishPayload);
                        await NotificationService.sendEvent({
                          type: 'TEST_SETTLEMENT',
                          payload: publishPayload,
                        });
                       const chatLabel = chatOptions.find(c => c.id === selectedChatId)?.label || 'Unknown';
                       setStatus({ type: 'success', text: `Published to ${chatLabel} chat (${selectedChatId})` });
                     } catch (err: any) {
                       setStatus({ type: 'error', text: err?.message || 'Failed to publish settlement.' });
                     } finally {
                       setSendingPreview(false);
                     }
                   }}
                    disabled={!beemPreviewResult || sendingPreview}
                   style={{ ...s.primaryBtn, marginTop: 0, flex: 1, backgroundColor: '#006855' }}
                 >
                   {sendingPreview ? <LoadingSpinner /> : 'Publish to Group'}
                 </button>
              </div>

             {beemPreviewResult && (
               <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                 <div style={{ backgroundColor: '#f5f7f8', borderRadius: 8, padding: 12 }}>
                   <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Group Chat Message (HTML)</div>
                   <pre style={{ fontSize: 12, color: '#2c2f30', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, fontFamily: 'inherit' }}>{beemPreviewResult.groupMsg}</pre>
                 </div>
                 <div style={{ backgroundColor: '#f5f7f8', borderRadius: 8, padding: 12 }}>
                   <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Private Nudge Message</div>
                   <pre style={{ fontSize: 12, color: '#2c2f30', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, fontFamily: 'inherit' }}>{beemPreviewResult.nudgeMsg}</pre>
                 </div>
                 <div style={{ backgroundColor: '#e8f5f0', borderRadius: 8, padding: 12 }}>
                   <div style={{ fontSize: 10, fontWeight: 700, color: '#006855', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Raw URL</div>
                   <code style={{ fontSize: 11, color: '#2c2f30', wordBreak: 'break-all' }}>{beemPreviewResult.url}</code>
                 </div>
                 <a
                   href={beemPreviewResult.url}
                   target="_blank"
                   rel="noopener noreferrer"
                   style={{ ...s.primaryBtn, textDecoration: 'none', marginTop: 0 }}
                 >
                   Open in Beem
                 </a>
               </div>
             )}
           </div>

           <div style={{ marginTop: 24, padding: 20, backgroundColor: '#fff', borderRadius: 16, border: '1px solid #e5e9ea' }}>
             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
               <div style={s.inputLabel}>Dispatch Diagnostics</div>
              <button
                onClick={() => { refreshDiag(); checkWorker(); }}
                style={{ fontSize: 10, fontWeight: 700, color: '#006855', cursor: 'pointer', background: 'none', border: 'none', textDecoration: 'underline' }}
              >
                Refresh
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#595c5d' }}>
                <span style={{ fontWeight: 700 }}>Auth</span>
                <span style={{ color: firebaseUser ? '#006855' : '#dc3545', fontWeight: 600 }}>
                  {firebaseUser ? `Logged in (${firebaseUser.email})` : 'Not authenticated'}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#595c5d' }}>
                <span style={{ fontWeight: 700 }}>Worker</span>
                <span style={{ fontWeight: 600 }}>
                  {checkingWorker ? '...' : workerOnline === null ? (
                    <button onClick={checkWorker} style={{ fontSize: 11, color: '#006855', cursor: 'pointer', background: 'none', border: 'none', textDecoration: 'underline' }}>Check</button>
                  ) : workerOnline ? (
                    <span style={{ color: '#006855' }}>Online</span>
                  ) : (
                    <span style={{ color: '#dc3545' }}>Unreachable</span>
                  )}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#595c5d' }}>
                <span style={{ fontWeight: 700 }}>Outbox</span>
                <span style={{ fontWeight: 600, color: outboxCount > 0 ? '#e65100' : '#006855' }}>
                  {outboxCount} pending
                </span>
              </div>
            </div>

            {diagEntries.length > 0 && (
              <div style={{ marginTop: 12, borderTop: '1px solid #e5e9ea', paddingTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                  Recent Dispatch Events
                </div>
                {diagEntries.slice().reverse().slice(0, 8).map((entry, i) => (
                  <div key={i} style={{
                    padding: '6px 8px',
                    marginBottom: 4,
                    borderRadius: 6,
                    backgroundColor: entry.result === 'success' ? '#e8f5f0' : entry.result === 'error' ? '#fce4e4' : '#f5f7f8',
                    fontSize: 11,
                    border: `1px solid ${entry.result === 'success' ? '#c8e6d0' : entry.result === 'error' ? '#f5c6c6' : '#e5e9ea'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontWeight: 700, color: entry.result === 'success' ? '#006855' : entry.result === 'error' ? '#dc3545' : '#595c5d' }}>
                        {entry.eventType}
                      </span>
                      <span style={{ color: '#999', fontSize: 10 }}>
                        {new Date(entry.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div style={{ color: '#595c5d' }}>
                      {entry.result === 'error' && <span style={{ color: '#dc3545' }}>{entry.error}</span>}
                      {entry.result === 'success' && 'Delivered to worker'}
                      {entry.workerStatus && ` (HTTP ${entry.workerStatus})`}
                    </div>
                    <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>
                      Auth: {entry.authTokenPresent ? 'token' : entry.adminFallback ? 'admin' : 'none'}
                      {entry.adminFallback && ' (dev admin)'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {diagEntries.length === 0 && (
              <div style={{ marginTop: 12, borderTop: '1px solid #e5e9ea', paddingTop: 12, fontSize: 12, color: '#999', textAlign: 'center' }}>
                No dispatch events recorded yet. Send a test message or trigger a buy-in.
              </div>
            )}
          </div>

          {status && (


          <div style={{ marginTop: 16 }}>
            <Banner type={status.type === 'success' ? 'info' : 'error'}>
              {status.text}
            </Banner>
          </div>
        )}
      </div>
    </div>
  );
}
